import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loggedRef = useRef(new Set());

  const insertAuditLog = async (authUser) => {
    if (!authUser?.id || !authUser?.email) return;
    if (loggedRef.current.has(authUser.id)) return;
    loggedRef.current.add(authUser.id);

    const metadata = {
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    try {
      const { error: rpcError } = await supabase.rpc('log_user_login', {
        p_user_id: authUser.id,
        p_email: authUser.email,
        p_metadata: metadata,
      });

      if (rpcError) {
        console.warn('RPC audit log failed, trying direct insert:', rpcError);
        await supabase.from('audit_logs').insert([{
          user_id: authUser.id,
          email: authUser.email,
          action: 'login',
          metadata,
        }]);
      }
    } catch (e) {
      console.error('Audit log failed:', e);
    }
  };

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user ?? null;
      setUser(nextUser);

      if (_event === 'SIGNED_IN' && nextUser) {
        setTimeout(() => {
          insertAuditLog(nextUser);
        }, 500);
      }

      if (_event === 'SIGNED_OUT') {
        loggedRef.current.clear();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, signIn, signOut, loading, isConfigured: isSupabaseConfigured }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);